import { ApolloClient, InMemoryCache, HttpLink, split, from } from '@apollo/client';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { setContext } from '@apollo/client/link/context';
import { getMainDefinition } from '@apollo/client/utilities';
import { onError } from '@apollo/client/link/error';
import { createClient } from 'graphql-ws';
import { createBrowserHistory } from 'history';
import gql from 'graphql-tag';

import { typePolicies, LinkConfig, ClientConfig, WSConfig } from './config.connections';

import { createErrorHandler } from './connection.handlers';
import { createTokenRefreshLink } from './token-refresh-link';
import { getAccessToken, hasAccessToken } from './storage';
import { SPYEE_TOKEN } from 'constants/index';
import { formApiUrl } from 'utils/url';

const httpLink = new HttpLink(LinkConfig);

export const cache = new InMemoryCache({ typePolicies });
export interface CacheFragmentReadArgs {
    typeName: string;
    id: string | number;
    fragment: string;
    fragmentArg: string;
}
export const readCacheFragment = ({ typeName, id, fragment: fragmentArg }: CacheFragmentReadArgs) => {
    return client.readFragment({
        id: `${typeName}:${id}`,
        fragment: gql`
            ${fragmentArg}
        `,
    });
};

export interface CacheFragmentWriteArgs {
    typeName: string;
    id: string | number;
    fragment: string;
    data: Record<string, any>;
}
export const writeCacheFragment = ({ typeName, id, fragment: fragmentArg, data: dataArg }: CacheFragmentWriteArgs) => {
    return client.writeFragment({
        id: `${typeName}:${id}`,
        fragment: gql`
            ${fragmentArg}
        `,
        data: {
            __typename: typeName,
            ...dataArg,
        },
    });
};

export const subscriptionClient = createClient(WSConfig);
const wsLink = new GraphQLWsLink(subscriptionClient);

const authenticationLink = setContext((_, { headers }) => {
    const authorization = getAccessToken() || '';
    const spiedusertoken = (hasAccessToken() && window.TOTEM.storage.get(SPYEE_TOKEN)) || '';

    return {
        headers: {
            ...headers,
            spiedusertoken,
            authorization,
            webapporigin: true,
        },
    };
});

function createApolloClient(history: ReturnType<typeof createBrowserHistory>) {
    const errorLink = onError(createErrorHandler(history));
    const tokenRefreshLink = createTokenRefreshLink(formApiUrl(), () => subscriptionClient.terminate());
    return new ApolloClient({
        link: from([
            tokenRefreshLink,
            errorLink,
            authenticationLink,
            split(
                ({ query }) => {
                    const definition = getMainDefinition(query);
                    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
                },
                wsLink,
                httpLink,
            ),
        ]),
        cache,
        ...ClientConfig,
    });
}

export const history = createBrowserHistory();
export const client = createApolloClient(history);
